import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";

const normalizePartnerSearch = (value = "") => value.toLowerCase().replace(/\s+/g, "");

const CouponModal = ({
  formOpen,
  resetForm,
  editingCouponId,
  form,
  setForm,
  updateFormField,
  fieldErrors,
  submitAttempted,
  discountError,
  handleSubmit,
  saving,
  partners,
  partnersLoading,
  onPartnerSearch,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerMenuOpen, setPartnerMenuOpen] = useState(false);
  const partnerOptions = useMemo(() => partners.map((partner) => ({
    value: partner._id,
    label: partner.companyName && partner.email
      ? `${partner.companyName} (${partner.email})`
      : partner.companyName || partner.email,
  })), [partners]);
  const selectedPartnerLabel = partnerOptions.find((option) => option.value === form.partnerId)?.label || "";
  const filteredPartnerOptions = useMemo(() => {
    const normalizedSearch = normalizePartnerSearch(partnerSearch);

    if (!normalizedSearch) {
      return partnerOptions;
    }

    return partnerOptions.filter((option) => (
      normalizePartnerSearch(option.label).includes(normalizedSearch)
    ));
  }, [partnerOptions, partnerSearch]);

  useEffect(() => {
    setPartnerSearch(selectedPartnerLabel);
  }, [selectedPartnerLabel]);

  return (
    <>
      <Modal show={formOpen} onHide={resetForm} centered size="lg" dialogClassName="coupon-modal-dialog">
        <Modal.Header closeButton className="coupon-modal-header border-0 pb-0">
          <div>
            <Modal.Title className="coupon-modal-title">
              {editingCouponId ? "Edit Coupon" : "Add Coupon"}
            </Modal.Title>
            <p className="coupon-modal-subtitle mb-0">
              Set up an agency or partner promo code for guest WhatsApp checkout.
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="coupon-section-card mb-3">
            <div className="coupon-section-heading">Basic Details</div>
            <Row className="g-3">
              <Col md={5} xs={12}>
                <label className="form-label coupon-label">Coupon Code</label>
                <input
                  type="text"
                  className={`form-control coupon-input ${submitAttempted && fieldErrors.code ? "is-invalid" : ""}`}
                  value={form.code}
                  onChange={(e) => updateFormField("code", e.target.value)}
                  placeholder="ABC10"
                />
                {submitAttempted && fieldErrors.code ? <small className="coupon-error-text">{fieldErrors.code}</small> : null}
                <small className="coupon-help-text">Generated from agency name and discount; edit if needed.</small>
              </Col>
              <Col md={7} xs={12}>
                <label className="form-label coupon-label">Partner</label>
                <div
                  className="coupon-partner-combobox"
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setPartnerMenuOpen(false);
                      setPartnerSearch(selectedPartnerLabel);
                    }
                  }}
                >
                  <input
                    type="text"
                    className={`form-control coupon-input ${submitAttempted && fieldErrors.partnerId ? "is-invalid" : ""}`}
                    value={partnerSearch}
                    onFocus={() => setPartnerMenuOpen(true)}
                    onClick={() => setPartnerMenuOpen(true)}
                    onChange={(event) => {
                      setPartnerSearch(event.target.value);
                      onPartnerSearch(event.target.value);
                      setPartnerMenuOpen(true);
                      if (form.partnerId) {
                        updateFormField("partnerId", "");
                      }
                    }}
                    placeholder={partnersLoading ? "Loading partners..." : "Select or search partners"}
                    disabled={partnersLoading}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className={`coupon-partner-toggle ${partnerMenuOpen ? "is-open" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setPartnerMenuOpen((open) => !open)}
                    disabled={partnersLoading}
                    aria-label="Show partners"
                  />
                  {partnerMenuOpen ? (
                    <div className="coupon-partner-menu" tabIndex={-1}>
                      {partnersLoading ? (
                        <div className="coupon-partner-option text-muted">Loading partners...</div>
                      ) : filteredPartnerOptions.length ? (
                        filteredPartnerOptions.map((option) => (
                          <button
                            type="button"
                            key={option.value}
                            className="coupon-partner-option"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              updateFormField("partnerId", option.value);
                              setPartnerSearch(option.label);
                              setPartnerMenuOpen(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="coupon-partner-option text-muted">No partners found</div>
                      )}
                    </div>
                  ) : null}
                </div>
                {submitAttempted && fieldErrors.partnerId ? <small className="coupon-error-text">{fieldErrors.partnerId}</small> : null}
                <small className="coupon-help-text">The partner name is retained on the coupon for historical display.</small>
              </Col>
            </Row>
          </div>

          <div className="coupon-section-card mb-3">
            <div className="coupon-section-heading">Commercial Rules</div>
            <Row className="g-3">
              <Col md={4} xs={12}>
                <label className="form-label coupon-label">Discount %</label>
                <input
                  type="number"
                  className={`form-control coupon-input ${((submitAttempted && fieldErrors.discountValue) || discountError) ? "is-invalid" : ""}`}
                  value={form.discountValue}
                  min="5"
                  max="100"
                  onChange={(e) => updateFormField("discountValue", e.target.value)}
                />
                {submitAttempted && fieldErrors.discountValue ? <small className="coupon-error-text">{fieldErrors.discountValue}</small> : null}
              </Col>
              <Col md={4} xs={12}>
                <label className="form-label coupon-label">Status</label>
                <select
                  className="form-select coupon-input"
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </Col>
              <Col md={4} xs={12}>
                <label className="form-label coupon-label">Channel</label>
                <select
                  className="form-select coupon-input"
                  value={form.sourceChannel}
                  onChange={(e) => setForm((prev) => ({ ...prev, sourceChannel: e.target.value }))}
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="website">Website</option>
                  <option value="all">All</option>
                </select>
              </Col>
            </Row>
          </div>

          <div className="coupon-section-card">
            <div className="coupon-section-heading">Limits and Validity</div>
            <Row className="g-3 align-items-end">
              <Col md={6} xs={12}>
                <label className="form-label coupon-label">Valid From</label>
                <input
                  type="date"
                  className={`form-control coupon-input ${submitAttempted && fieldErrors.validFrom ? "is-invalid" : ""}`}
                  value={form.validFrom}
                  min={today}
                  onChange={(e) =>
                    setForm((prev) => {
                      const nextValidFrom = e.target.value;

                      return {
                        ...prev,
                        validFrom: nextValidFrom,
                        validTo:
                          prev.validTo && nextValidFrom && prev.validTo < nextValidFrom
                            ? ""
                            : prev.validTo,
                      };
                    })
                  }
                />
                {submitAttempted && fieldErrors.validFrom ? <small className="coupon-error-text">{fieldErrors.validFrom}</small> : null}
              </Col>
              <Col md={6} xs={12}>
                <label className="form-label coupon-label">Valid To</label>
                <input
                  type="date"
                  className={`form-control coupon-input ${submitAttempted && fieldErrors.validTo ? "is-invalid" : ""}`}
                  value={form.validTo}
                  min={form.validFrom || undefined}
                  disabled={!form.validFrom}
                  onChange={(e) => setForm((prev) => ({ ...prev, validTo: e.target.value }))}
                />
                {submitAttempted && fieldErrors.validTo ? <small className="coupon-error-text">{fieldErrors.validTo}</small> : null}
              </Col>
              <Col xs={12}>
                <div className="coupon-toggle-row">
                  <div>
                    <div className="coupon-toggle-title">One use per customer</div>
                    <div className="coupon-toggle-subtitle">Multiple customers can use this coupon, but each customer can use it only once after successful payment.</div>
                  </div>
                  <div className="form-check m-0 coupon-check-wrap">
                    <input
                      className="form-check-input coupon-check-input"
                      type="checkbox"
                      checked={form.oneTimeUse}
                      onChange={(e) => setForm((prev) => ({ ...prev, oneTimeUse: e.target.checked }))}
                      id="coupon-one-time"
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <button className="btn btn-outline-secondary" onClick={resetForm} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary px-4" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : editingCouponId ? "Update Coupon" : "Create Coupon"}
          </button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .coupon-modal-dialog .modal-content {
          border: 0;
          border-radius: 20px;
          box-shadow: 0 24px 80px rgba(16, 24, 40, 0.18);
          overflow: hidden;
        }

        .coupon-modal-header,
        .coupon-modal-dialog .modal-body,
        .coupon-modal-dialog .modal-footer {
          padding-left: 24px;
          padding-right: 24px;
        }

        .coupon-modal-title {
          font-size: 2rem;
          font-weight: 700;
          color: #172b4d;
          letter-spacing: -0.02em;
        }

        .coupon-modal-subtitle {
          margin-top: 6px;
          color: #667085;
          font-size: 0.95rem;
        }

        .coupon-section-card {
          background: linear-gradient(180deg, #fbfcff 0%, #f7f9fc 100%);
          border: 1px solid #e6ebf2;
          border-radius: 16px;
          padding: 18px;
        }

        .coupon-section-heading {
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #215b5b;
          margin-bottom: 14px;
        }

        .coupon-label {
          font-weight: 600;
          color: #243b53;
          margin-bottom: 8px;
        }

        .coupon-input {
          min-height: 48px;
          border-radius: 12px;
          border: 1px solid #d7deea;
          box-shadow: none;
        }

        .coupon-input:focus {
          border-color: #215b5b;
          box-shadow: 0 0 0 3px rgba(33, 91, 91, 0.12);
        }

        .coupon-partner-combobox {
          position: relative;
        }

        .coupon-partner-combobox .coupon-input {
          padding-right: 48px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .coupon-partner-toggle {
          position: absolute;
          top: 1px;
          right: 1px;
          width: 46px;
          height: 46px;
          border: 0;
          border-radius: 0 12px 12px 0;
          background: transparent;
          color: #52606d;
          font-size: 18px;
          line-height: 1;
        }

        .coupon-partner-toggle::after {
          content: "";
          display: inline-block;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 6px solid #52606d;
          transition: transform 0.15s ease;
        }

        .coupon-partner-toggle.is-open::after {
          transform: rotate(180deg);
        }

        .coupon-partner-toggle:disabled {
          opacity: 0.55;
        }

        .coupon-partner-menu {
          position: absolute;
          z-index: 1070;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          max-height: 220px;
          overflow-y: auto;
          border: 1px solid #d7deea;
          border-radius: 12px;
          background: #ffffff;
          box-shadow: 0 16px 36px rgba(16, 24, 40, 0.16);
          padding: 6px;
        }

        .coupon-partner-option {
          display: block;
          width: 100%;
          border: 0;
          border-radius: 8px;
          background: transparent;
          color: #243b53;
          font-size: 0.92rem;
          line-height: 1.3;
          text-align: left;
          padding: 10px 12px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .coupon-partner-option:hover,
        .coupon-partner-option:focus {
          background: #eef7f7;
          outline: none;
        }

        .coupon-help-text {
          display: block;
          margin-top: 8px;
          color: #7b8794;
          font-size: 0.82rem;
        }

        .coupon-error-text {
          display: block;
          margin-top: 8px;
          color: #d92d20;
          font-size: 0.82rem;
          font-weight: 500;
        }

        .coupon-toggle-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          border: 1px solid #d7deea;
          border-radius: 14px;
          background: #ffffff;
          padding: 14px 16px;
          margin-top: 4px;
        }

        .coupon-toggle-title {
          font-weight: 600;
          color: #243b53;
        }

        .coupon-toggle-subtitle {
          color: #7b8794;
          font-size: 0.84rem;
          margin-top: 4px;
        }

        .coupon-toggle-row .form-check-input {
          margin-top: 0;
          cursor: pointer;
        }

        .coupon-check-wrap {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .coupon-check-input {
          width: 20px;
          height: 20px;
          border-radius: 6px;
          border: 1px solid #b8c4d6;
          box-shadow: none;
        }

        .coupon-check-input:checked {
          background-color: #2563eb;
          border-color: #2563eb;
        }

        .coupon-check-input:focus {
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
          border-color: #2563eb;
        }

        @media (max-width: 767px) {
          .coupon-modal-title {
            font-size: 1.55rem;
          }

          .coupon-modal-header,
          .coupon-modal-dialog .modal-body,
          .coupon-modal-dialog .modal-footer {
            padding-left: 18px;
            padding-right: 18px;
          }
        }
      `}</style>
    </>
  );
};

export default CouponModal;
